import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom-v5-compat';
import ModalPortal from '~/components/views/ModalPortal/ModalPortal';
import GoogleAnalyticsConstants from '~/constants/google-analytics-constants';
import useMediaQuery from '~/hooks/use-media-query';
import { deviceMedia } from '~/styled';
import { InModalMessage } from './components/InModalMessages';
import ModalActions from './components/ModalActions';
import ModalBodyContentBox from './components/ModalBodyContentBox';
import ModalHeader from './components/ModalHeader';
import * as S from './styled';
import { ModalContentProps, PortalModalRef } from './types';

export const PortalModal = forwardRef<PortalModalRef, ModalContentProps>(
  (
    {
      isOpen,
      onClose,
      title: titleLang,
      children,
      infoOrErrorOrWarning = false,
      closeOnEscape = true,
      closeOnBackdropClick = true,
      enforcedCloseOnBackdropClick,
      actions,
      header,
      actionButtons = [],
      hideHeader = false,
      hideFooter = false,
      className,
      contentClassName,
      isFormWrapper,
      showCloseIcon,
      onFormSubmit,
      titleIcon,
      formAttributes,
      testId,
      trackingLabel,
      backBtnProps,
      containerWidth,
      inModalMessage,
      headerAction,
      fullScreenMediaBreakpoint = 's',
      id,
      willBeGraySurface = false,
      customStyles,
    },
    ref
  ) => {
    const location = useLocation();
    const initialPathRef = useRef(location.pathname);
    const isMobile = useMediaQuery(deviceMedia.down[fullScreenMediaBreakpoint]);
    const modalBackdropRef = useRef<HTMLDivElement>(null);
    const ModalBodyContainerRef = useRef<HTMLDivElement | HTMLFormElement>(null);
    const ModalContentContainerRef = useRef<HTMLDivElement>(null);
    const [containerInnerWidth, setContainerInnerWidth] = useState<number | null>(null);

    // Close modal on route change e.g user click on back button
    useEffect(() => {
      if (onClose && isOpen && location.pathname !== initialPathRef.current) {
        onClose();
      }
    }, [location.pathname]);

    const handleKeyDown = React.useCallback(
      (event: KeyboardEvent) => {
        if (isMobile) {
          return;
        }
        if (event.key === 'Escape' && closeOnEscape && onClose) {
          onClose();
        }
      },
      [closeOnEscape, onClose, isMobile]
    );

    const handleBackdropClick = React.useCallback(
      (event: React.MouseEvent) => {
        if (
          (closeOnBackdropClick || enforcedCloseOnBackdropClick) &&
          event.target === modalBackdropRef.current &&
          onClose
        ) {
          event.preventDefault();
          if (isMobile && !enforcedCloseOnBackdropClick) {
            return;
          }
          onClose();
        }
      },
      [closeOnBackdropClick, enforcedCloseOnBackdropClick, onClose, isMobile]
    );

    React.useEffect(() => {
      if (isOpen) {
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', handleKeyDown);
      } else {
        document.body.style.overflow = 'auto';
      }

      return () => {
        document.body.style.overflow = 'auto';
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [isOpen, handleKeyDown]);

    useEffect(() => {
      const handleResize = () => {
        if (ModalBodyContainerRef.current) {
          setContainerInnerWidth(ModalBodyContainerRef.current.clientWidth);
        }
      };
      window.addEventListener('resize', handleResize);
      handleResize();

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, [ModalBodyContainerRef.current?.clientWidth]);

    useImperativeHandle(
      ref,
      () => ({
        close: onClose,
        isOpen,
        modalRef: ModalBodyContainerRef,
        contentContainerRef: ModalContentContainerRef,
      }),
      [onClose, isOpen, ModalBodyContainerRef, ModalContentContainerRef]
    );

    if (!isOpen) {
      return null;
    }

    const hasFooter = !hideFooter && Boolean(actions || actionButtons.length > 0);

    return (
      <ModalPortal>
        <S.ModalBackdrop
          id={id}
          ref={modalBackdropRef}
          onClick={handleBackdropClick}
          className={className}
          data-testid={testId}
          data-trackinglabel={trackingLabel ?? GoogleAnalyticsConstants.categories.MODAL}
        >
          <ModalBodyContentBox
            isFormWrapper={Boolean(isFormWrapper)}
            onFormSubmit={onFormSubmit}
            infoOrErrorOrWarning={infoOrErrorOrWarning}
            formProps={formAttributes}
            containerWidth={containerWidth}
            contentContainerRef={ModalBodyContainerRef}
            modalId={id}
            willBeGraySurface={willBeGraySurface}
            customStyled={customStyles?.contentContainer}
          >
            <ModalHeader
              hideHeader={hideHeader}
              header={header}
              infoOrErrorOrWarning={infoOrErrorOrWarning}
              titleLang={{ key: titleLang?.key ?? '', values: titleLang?.values }}
              isMobile={isMobile}
              backBtnProps={backBtnProps}
              onClose={onClose}
              showCloseIcon={showCloseIcon}
              titleIcon={titleIcon}
              center={titleLang?.center}
              bottomBorder={titleLang?.bottomBorder}
              headerAction={headerAction}
              id={id}
            />

            <S.InModalMessageContainer id="top-message-container" />
            <S.ModalContentDiv
              id={id ? `${id}-modal-content` : 'modal-content'}
              className={contentClassName}
              $infoOrErrorOrWarning={infoOrErrorOrWarning}
              $hasFooter={hasFooter}
              ref={ModalContentContainerRef}
            >
              {children}
            </S.ModalContentDiv>
            <S.InModalMessageContainer id="bottom-message-container" />
            {!hideFooter && (
              <ModalActions
                actions={actions}
                actionButtons={actionButtons}
                isMobile={isMobile}
                id={id}
                infoOrErrorOrWarning={infoOrErrorOrWarning}
              />
            )}
          </ModalBodyContentBox>
        </S.ModalBackdrop>
        {inModalMessage && (
          <InModalMessage modalMessage={inModalMessage} containerWidth={containerInnerWidth} />
        )}
      </ModalPortal>
    );
  }
);

PortalModal.displayName = 'PortalModal';
export default PortalModal;
